import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase.ts";
import { useSession } from "../lib/session.tsx";
import type { StringKey } from "../lib/i18n.ts";

/**
 * First-login welcome and the guided tour of the whole app.
 *
 * The welcome opens once for an account provisioned with welcomePending (see
 * functions/scripts/provision-username-user.ts) and points straight at the
 * race analysis and the playbook when the person may read them. The tour walks
 * every area the person's permissions open — steps for pages they cannot use
 * are skipped — and can be relaunched any time from the Guide (/?tour=1).
 */
interface TourStep {
  id: string;
  route: string;
  needs: readonly string[];
  titleKey: StringKey;
  bodyKey: StringKey;
}

const TOUR_STEPS: readonly TourStep[] = [
  {
    id: "dashboard",
    route: "/",
    needs: [],
    titleKey: "tour.dashboard.title",
    bodyKey: "tour.dashboard.body",
  },
  {
    id: "strategy",
    route: "/strategy",
    needs: ["strategy.view"],
    titleKey: "tour.strategy.title",
    bodyKey: "tour.strategy.body",
  },
  {
    id: "advisor",
    route: "/advisor",
    needs: ["aura.query"],
    titleKey: "tour.advisor.title",
    bodyKey: "tour.advisor.body",
  },
  {
    id: "memory",
    route: "/memory",
    needs: ["aura.query"],
    titleKey: "tour.memory.title",
    bodyKey: "tour.memory.body",
  },
  {
    id: "evidence",
    route: "/evidence",
    needs: [],
    titleKey: "tour.evidence.title",
    bodyKey: "tour.evidence.body",
  },
  {
    id: "claims",
    route: "/claims-review",
    needs: ["aura.canonical.approve"],
    titleKey: "tour.claims.title",
    bodyKey: "tour.claims.body",
  },
  {
    id: "watchdog",
    route: "/watchdog",
    needs: ["aura.query"],
    titleKey: "tour.watchdog.title",
    bodyKey: "tour.watchdog.body",
  },
  {
    id: "warRoom",
    route: "/war-room",
    needs: ["aura.query"],
    titleKey: "tour.warRoom.title",
    bodyKey: "tour.warRoom.body",
  },
  {
    id: "field",
    route: "/field",
    needs: [],
    titleKey: "tour.field.title",
    bodyKey: "tour.field.body",
  },
  {
    id: "contacts",
    route: "/contacts",
    needs: ["crm.view"],
    titleKey: "tour.contacts.title",
    bodyKey: "tour.contacts.body",
  },
  {
    id: "projects",
    route: "/projects",
    needs: ["projects.view"],
    titleKey: "tour.projects.title",
    bodyKey: "tour.projects.body",
  },
  {
    id: "calendar",
    route: "/calendar",
    needs: ["projects.view"],
    titleKey: "tour.calendar.title",
    bodyKey: "tour.calendar.body",
  },
  {
    id: "board",
    route: "/board",
    needs: [],
    titleKey: "tour.board.title",
    bodyKey: "tour.board.body",
  },
  {
    id: "report",
    route: "/report",
    needs: ["projects.view"],
    titleKey: "tour.report.title",
    bodyKey: "tour.report.body",
  },
  {
    id: "hermes",
    route: "/hermes",
    needs: ["hermes.run"],
    titleKey: "tour.hermes.title",
    bodyKey: "tour.hermes.body",
  },
  {
    id: "finance",
    route: "/finance",
    needs: ["finance.view"],
    titleKey: "tour.finance.title",
    bodyKey: "tour.finance.body",
  },
  {
    id: "guide",
    route: "/guide",
    needs: [],
    titleKey: "tour.guide.title",
    bodyKey: "tour.guide.body",
  },
];

type Mode = "closed" | "welcome" | "tour";

export function Onboarding() {
  const { profile, can, t, demoMode } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>("closed");
  const [stepIndex, setStepIndex] = useState(0);
  const steps = TOUR_STEPS.filter((s) => s.needs.length === 0 || s.needs.some((p) => can(p)));
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const pending = profile?.welcomePending === true && !demoMode;
  const route = step?.route;

  useEffect(() => {
    if (pending) setMode((m) => (m === "closed" ? "welcome" : m));
  }, [pending]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get("tour") === "1") {
      setStepIndex(0);
      setMode("tour");
    }
  }, [location.search]);

  useEffect(() => {
    if (mode === "tour" && route) navigate(route);
  }, [mode, route, navigate]);

  function finish() {
    setMode("closed");
    if (profile?.welcomePending === true) {
      void updateDoc(doc(db, "users", profile.uid), { welcomePending: false }).catch(() => {
        /* Non-fatal: the popup simply shows again next session. */
      });
    }
  }

  function openDoc(docId: "race-analysis" | "playbook") {
    finish();
    navigate(`/strategy?doc=${docId}`);
  }

  if (mode === "closed" || demoMode) return null;
  const firstName = (profile?.displayName ?? "").split(" ")[0] ?? "";

  if (mode === "welcome") {
    return (
      <div
        className="demo-tour-veil"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
      >
        <aside className="demo-tour-panel">
          <div className="demo-tour-topline">
            <span>ARTURO CAMPAIGNOS</span>
            <button type="button" aria-label={t("tour.close")} onClick={finish}>
              ×
            </button>
          </div>
          <div className="demo-tour-copy">
            <div className="demo-tour-eyebrow">{t("welcome.eyebrow")}</div>
            <h2 id="welcome-title">{t("welcome.title", { name: firstName })}</h2>
            <p>{can("strategy.view") ? t("welcome.lede") : t("welcome.ledeGeneric")}</p>
          </div>
          {can("strategy.view") && (
            <div style={{ display: "grid", gap: 10, margin: "14px 0" }}>
              <button
                type="button"
                className="primary"
                style={{ padding: "16px 18px", textAlign: "left" }}
                onClick={() => openDoc("race-analysis")}
              >
                <span style={{ display: "block", fontSize: "1.1rem", fontWeight: 700 }}>
                  {t("welcome.raceAnalysis")}
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: "0.9rem",
                    fontWeight: 400,
                    opacity: 0.9,
                    marginTop: 4,
                  }}
                >
                  {t("welcome.raceAnalysisHint")}
                </span>
              </button>
              <button
                type="button"
                className="primary"
                style={{ padding: "16px 18px", textAlign: "left" }}
                onClick={() => openDoc("playbook")}
              >
                <span style={{ display: "block", fontSize: "1.1rem", fontWeight: 700 }}>
                  {t("welcome.playbook")}
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: "0.9rem",
                    fontWeight: 400,
                    opacity: 0.9,
                    marginTop: 4,
                  }}
                >
                  {t("welcome.playbookHint")}
                </span>
              </button>
            </div>
          )}
          <div className="demo-tour-actions">
            <button type="button" className="subtle" onClick={finish}>
              {t("welcome.explore")}
            </button>
            <button
              type="button"
              onClick={() => {
                setStepIndex(0);
                setMode("tour");
              }}
            >
              {t("welcome.tour")}
            </button>
          </div>
        </aside>
      </div>
    );
  }

  if (!step) return null;
  return (
    <div className="demo-tour-veil" role="dialog" aria-modal="true" aria-labelledby="tour-title">
      <aside className="demo-tour-panel">
        <div className="demo-tour-topline">
          <span>ARTURO CAMPAIGNOS</span>
          <button type="button" aria-label={t("tour.close")} onClick={finish}>
            ×
          </button>
        </div>
        <div className="demo-tour-progress">
          {steps.map((s, index) => (
            <button
              key={s.id}
              type="button"
              className={index === stepIndex ? "active" : ""}
              aria-label={t(s.titleKey)}
              onClick={() => setStepIndex(index)}
            />
          ))}
        </div>
        <div className="demo-tour-copy">
          <div className="demo-tour-eyebrow">
            {t("tour.step", { n: stepIndex + 1, total: steps.length })}
          </div>
          <h2 id="tour-title">{t(step.titleKey)}</h2>
          <p>{t(step.bodyKey)}</p>
        </div>
        <div className="demo-tour-actions">
          <button type="button" className="subtle" onClick={finish}>
            {t("tour.skip")}
          </button>
          <div>
            {stepIndex > 0 && (
              <button type="button" onClick={() => setStepIndex(stepIndex - 1)}>
                {t("tour.back")}
              </button>
            )}
            {stepIndex < steps.length - 1 ? (
              <button type="button" className="primary" onClick={() => setStepIndex(stepIndex + 1)}>
                {t("tour.next")}
              </button>
            ) : (
              <button type="button" className="primary" onClick={finish}>
                {t("tour.finish")}
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
